/*
  Interactivity helpers extracted from DopeOS so that views can register
  buttons without importing React components.
*/

import { DopeTextRenderer } from './renderer/bitmapFont';
// Platform detection – needed so Farcaster mini-apps inherit mobile hit-box inflation
import { isMobileDevice } from './device.js';

export function navigateTo(view) {
  if (typeof window === 'undefined' || !view) return;
  window.__DCW_REQUESTED_VIEW_CLEARED = true;
  if (typeof window.DCW_showView === 'function') {
    window.DCW_showView(view);
    return;
  }
  window.dispatchEvent(new CustomEvent('dcw:navigate', { detail: { view } }));
}

export const interactiveRegistry = {
  elements: [],
  focusedIndex: -1,
  focusedId: null,
  hoveredIndex: -1,
  hoveredId: null,
  clear() {
    this.elements = [];
    this.focusedIndex = -1;
    this.hoveredIndex = -1;
  },
  register(element) {
    /*
      Mobile/Farcaster tap-target inflation
      ------------------------------------
      1. *Only* inflate *small* controls (<= 80 px). Large scroll areas and
         wide quick-reply buttons already occupy enough space and do **not**
         need extra padding — doing so causes overlaps.
      2. Use a gentler 40 % padding (was 60 %) which, combined with the
         minimum-tap fallback, yields ~50-55 px hit-boxes for 24-48 px icons.
    */
    // Allow callers (e.g. close-button, leaderboard icons) to OPT-OUT of the
    // automatic mobile inflation by setting `skipInflation: true` on the
    // element descriptor.
    const el = {
      skipInflation: false,
      focusable: typeof element.onClick === 'function',
      ...element,
    };

    const SMALL_THRESHOLD = 80; // buffer units ≈ CSS px at 1× scaler

    if (!el.skipInflation && isMobileEnv() && el.w <= SMALL_THRESHOLD && el.h <= SMALL_THRESHOLD) {
      // Use a smaller, more precise padding for mobile to avoid overlapping
      const PAD_RATIO = 0.15; // 15% each direction (was 25%)

      const padX = el.w * PAD_RATIO * 0.5;
      const padY = el.h * PAD_RATIO * 0.5;
      el.x = Math.max(0, el.x - padX);
      el.y = Math.max(0, el.y - padY);
      el.w += padX * 2;
      el.h += padY * 2;

      // Enforce WCAG/HIG minimum 44 px tap-target
      const MIN_TAP = 44;
      // Only apply min-tap if it doesn't cause massive overlap (check distance to neighbors?)
      // For now, we trust the layout logic, but be conservative.
      if (el.w < MIN_TAP) {
        const extra = (MIN_TAP - el.w) / 2;
        el.x = Math.max(0, el.x - extra);
        el.w = MIN_TAP;
      }
      if (el.h < MIN_TAP) {
        const extra = (MIN_TAP - el.h) / 2;
        el.y = Math.max(0, el.y - extra);
        el.h = MIN_TAP;
      }
    }

    this.elements.push(el);
    const index = this.elements.length - 1;

    if (el.id && el.id === this.focusedId && el.focusable) {
      this.focusedIndex = index;
    }
    if (el.id && el.id === this.hoveredId) {
      this.hoveredIndex = index;
    }

    return index;
  },
  getFocusableElements() {
    return this.elements.filter(el => el.focusable);
  },
  setFocusByIndex(idx) {
    this.focusedIndex = idx;
    this.focusedId = idx >= 0 ? (this.elements[idx]?.id ?? null) : null;
  },
  setHoverByIndex(idx) {
    this.hoveredIndex = idx;
    this.hoveredId = idx >= 0 ? (this.elements[idx]?.id ?? null) : null;
  },
  getFocused() {
    return this.focusedIndex >= 0 && this.focusedIndex < this.elements.length
      ? this.elements[this.focusedIndex]
      : null;
  },
  getHovered() {
    return this.hoveredIndex >= 0 && this.hoveredIndex < this.elements.length
      ? this.elements[this.hoveredIndex]
      : null;
  },
};

// Simple CRT-green button with border and transparent interior
export function drawInteractiveRect(ctx, { x, y, w, h }) {
  ctx.save();
  ctx.globalAlpha = 0.95;
  ctx.fillStyle = '#000';
  ctx.fillRect(x, y, w, h);
  ctx.strokeStyle = '#39FF14';
  // Canonical stroke width: 3 px across all buttons (was 2)
  const lineW = 3;
  ctx.lineWidth = lineW;
  const offset = lineW / 2; // centre stroke (1 px for 2-px border)
  ctx.strokeRect(x + offset, y + offset, w - lineW, h - lineW);
  ctx.restore();
}

// Unified mobile helper (see src/engine/device.js)
function isMobileEnv() {
  return isMobileDevice();
}

// ─── Canonical Close Button Renderer ─────────────────────────────────────────
// Draws the standard 24×24 DOS-style X button and registers its hit-box.
// Returns the registry element { id, x, y, w, h } so callers can track focus.
export function renderCloseButton(ctx, safeArea, {
  id = 'close-btn',
  onClick = () => {},
  hiddenOnMobile = true,
} = {}) {
  // Hide on mobile if requested (users swipe down to close modals)
  const isMobile = isMobileEnv();
  if (hiddenOnMobile && isMobile) return null;

  // Make the button larger on mobile to provide a more comfortable tap target
  // **without** relying solely on invisible hit-box inflation.  This preserves
  // canonical hit-box maths for the rest of the UI while improving perceived
  // usability.
  // Compute mobile size proportional to buffer width (~10 %) so physical size ~40-50 px CSS.
  // Size heuristic: ~12 % of safe-area width so the drawn box is very close to
  // the (inflated) hit-box on all devices.
  const dynamicSize = Math.round(safeArea.width * 0.12); // e.g. 1080 ⇒ 130 px buffer → ~47-48 px CSS

  // Visual box size – noticeably larger on desktop as well so users “feel” the
  // same target they can click/tap.  Mobile still clamps to sane limits.
  const BOX_PX = isMobile
    ? Math.min(Math.max(dynamicSize, 64), 180) // minimum 64 px on phones/tablets
    : 42; // desktop size tuned down (was 48)

  // ── Compensation for CRT curvature ──────────────────────────────
  // Pixels rendered near the very top-right corner are visually shifted
  // inwards by the shader’s barrel-distortion.  We therefore *expand* the
  // interactive region further towards the centre of the screen to ensure
  // the user can hit the “X” exactly where they see it.  Make this
  // proportional to the button so it adapts automatically when we tweak
  // BOX_PX.
  const MARGIN_PX = 8;
  const closeX = Math.round(safeArea.left + safeArea.width - BOX_PX - MARGIN_PX);
  const closeY = Math.round(safeArea.top + MARGIN_PX);

  // Dynamic barrel-distortion compensation — grows with distance from centre.
  const safeCenterX = safeArea.left + safeArea.width / 2;
  const safeCenterY = safeArea.top + safeArea.height / 2;
  const btnCenterX = closeX + BOX_PX / 2;
  const btnCenterY = closeY + BOX_PX / 2;

  const normX = (btnCenterX - safeCenterX) / (safeArea.width / 2); // −1…1
  const normY = (btnCenterY - safeCenterY) / (safeArea.height / 2);

  // Max 26 % horizontal, 14 % vertical at full edge (visually tuned)
  const CURVE_COMP_X = Math.round(BOX_PX * 0.26 * Math.abs(normX));
  const CURVE_COMP_Y = Math.round(BOX_PX * 0.14 * Math.abs(normY));

  // Sign determines shift towards centre (norm>0 means right/bottom side)
  const shiftX = CURVE_COMP_X * Math.sign(normX);
  const shiftY = CURVE_COMP_Y * Math.sign(normY);

  // Draw base rectangle + border using existing helper
  drawInteractiveRect(ctx, { x: closeX, y: closeY, w: BOX_PX, h: BOX_PX });

  // Draw centred "X" label – scale proportionally to box size
  const label = 'X';
  // Base reference: 24-px box uses scale ≈1.4.  Derive scale linearly.
  const SCALE = +(BOX_PX / 17).toFixed(2); // keep two decimals for stability
  const metrics = DopeTextRenderer.measureDOSText(label, SCALE);
  const glyphW = metrics.width / 1.2; // compensate charSpacing
  const baselineAdj = Math.round(metrics.height * 0.2);
  DopeTextRenderer.renderDOSText(
    ctx,
    label,
    closeX + (BOX_PX - glyphW) / 2,
    closeY + (BOX_PX - metrics.height) / 2 + baselineAdj,
    SCALE,
  );

  // Interactive region – inflate for mobile + CRT curvature (unchanged)
  const extraHit = isMobile ? 8 : 6; // widen padding further on touch devices
  const el = interactiveRegistry.register({
    id,
    // Shift slightly further LEFT and DOWN (towards screen centre) to match
    // the barrel-distorted rendering while keeping the region inside bounds.
    x: closeX - extraHit - shiftX,
    y: closeY - extraHit - shiftY,
    w: BOX_PX + extraHit * 2 + Math.abs(shiftX),
    h: BOX_PX + extraHit * 2 + Math.abs(shiftY),
    onClick,
    focusable: true,
    skipInflation: true, // Already manually enlarged & compensated
  });

  // Ensure the close-button is always the LAST element in the registry so it
  // receives pointer events even when other (larger) interactive regions
  // overlap (e.g. leaderboard/chat scroll areas).
  // We move the element we just registered to the end of the array.
  const elementsArr = interactiveRegistry.elements;
  if (elementsArr.length > 1) {
    const last = elementsArr.splice(elementsArr.length - 1, 1)[0]; // close-btn element
    elementsArr.push(last); // now guaranteed last
  }

  // ── Clamp hit-box inside safe-area ──────────────────────────────
  const safeRight = safeArea.left + safeArea.width;
  const safeBottom = safeArea.top + safeArea.height;
  const overflowX = (el.x + el.w) - safeRight;
  const overflowY = (el.y + el.h) - safeBottom;
  if (overflowX > 0) {
    el.x -= overflowX;
  }
  if (overflowY > 0) {
    el.y -= overflowY;
  }
  return el;
}

// ─── Canonical Scroll Bar Renderer ───────────────────────────────────────────
export function drawScrollbar(ctx, {
  trackX, trackY, trackW, trackH,
  contentHeight, viewportHeight, scrollOffset,
}) {
  // Skip if scrolling not needed
  if (contentHeight <= viewportHeight) return;

  ctx.save();
  ctx.fillStyle = '#001800';
  ctx.fillRect(trackX, trackY, trackW, trackH);

  const thumbH = Math.max(16, (viewportHeight / contentHeight) * trackH);
  const ratio = scrollOffset / (contentHeight - viewportHeight);
  const thumbY = trackY + ratio * (trackH - thumbH);
  ctx.fillStyle = '#39FF14';
  ctx.fillRect(trackX, thumbY, trackW, thumbH);
  ctx.restore();
}

export function drawDebugHitboxes(ctx, color = 'rgba(255,0,0,0.5)') {
  if (!ctx) return;
  ctx.save();
  ctx.strokeStyle = color;
  ctx.lineWidth = 2;
  interactiveRegistry.elements.forEach(el => {
    ctx.strokeRect(el.x, el.y, el.w, el.h);
  });
  ctx.restore();
} 